import { ServiceWorkerUpdateMessageType } from 'shared/domain/messages/serviceWorkerUpdate';
import { AlignmentStage } from './types';

/** Waits until the worker reaches `state`; `false` when it became redundant or the time ran out. */
export function waitForServiceWorkerState(
  worker: ServiceWorker,
  state: ServiceWorkerState,
  timeoutMs: number
): Promise<boolean> {
  if (worker.state === state) {
    return Promise.resolve(true);
  }
  if (worker.state === 'redundant') {
    // Nothing more will happen to it: the listener below would wait for the whole time.
    return Promise.resolve(false);
  }
  return new Promise((resolve) => {
    const finish = (reached: boolean): void => {
      clearTimeout(timer);
      worker.removeEventListener('statechange', onStateChange);
      resolve(reached);
    };
    const onStateChange = (): void => {
      if (worker.state === state) {
        finish(true);
      } else if (worker.state === 'redundant') {
        finish(false);
      }
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    worker.addEventListener('statechange', onStateChange);
  });
}

/**
 * Makes a newer worker (installing or waiting) take over and resolves once it
 * is active; `false` when it did not get there within `timeoutMs`. The tabs
 * of the previous version reload themselves when it takes control.
 * `onStage` tells what it waits for, so a caller that gives up on the time
 * knows where it stood.
 */
export async function activateWaitingServiceWorker(
  worker: ServiceWorker,
  timeoutMs: number,
  onStage: (stage: AlignmentStage.installing | AlignmentStage.takingOver) => void = () => undefined
): Promise<boolean> {
  const startedAt = Date.now();
  if (worker.state === 'installing') {
    onStage(AlignmentStage.installing);
    if (!(await waitForServiceWorkerState(worker, 'installed', timeoutMs))) {
      return false;
    }
  }
  onStage(AlignmentStage.takingOver);
  worker.postMessage({ type: ServiceWorkerUpdateMessageType.skipWaiting });
  return waitForServiceWorkerState(worker, 'activated', Math.max(timeoutMs - (Date.now() - startedAt), 0));
}
